import { useIntl } from 'react-intl';
import { Rule } from 'rc-field-form/lib/interface';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import { MansLauksProduct } from '../ui/Drawer/Drawers/DrawerMansLauksProducts';
import dayjs from 'dayjs';

type ValidationOptions = {
  validation: string;
  min?: number;
  max?: number;
};

function useFormValidation() {
  const intl = useIntl();
  const form = useFormInstance();

  const minMaxValidator = ({
    validation,
    min,
    max,
  }: ValidationOptions): Rule => {
    switch (validation) {
      case 'maxLength':
        return {
          max,
          message: intl.formatMessage({ id: 'validation.max_length' }) + max,
        };
      case 'minLength':
        return {
          min,
          message: intl.formatMessage({ id: 'validation.min_length' }) + min,
        };
      default:
        return {};
    }
  };

  const nogaStorageStockCountNumberCheck = (product: MansLauksProduct) => {
    return async (_: any, value: any) => {
      const mansLauksStorageStockCount = product.amount;
      const mansLauksUnit = product.unit;
      const nonDecimalUnits = ['iep', 'gab'];

      if (value < 0) {
        return Promise.reject(
          new Error(
            intl.formatMessage({
              id: 'validation.value_must_be_positive',
            })
          )
        );
      }

      if (nonDecimalUnits.includes(mansLauksUnit) && value % 1 !== 0) {
        return Promise.reject(
          new Error(
            intl.formatMessage({
              id: 'validation.value_must_not_be_decimal',
            })
          )
        );
      }

      if (value > mansLauksStorageStockCount) {
        return Promise.reject(
          new Error(
            intl.formatMessage({
              id: 'validation.value_bigger_than_storage_stock_count',
            })
          )
        );
      }

      if (!value) {
        return Promise.reject(
          new Error(intl.formatMessage({ id: 'validation.please_enter_value' }))
        );
      }

      await Promise.resolve();
    };
  };

  const handleValidations = (validation: string): Rule => {
    switch (validation) {
      case 'required':
        return {
          required: true,
          message: intl.formatMessage({ id: 'validation.required' }),
        };
      case 'regNr':
        return {
          pattern: /^\d{11}$/,
          message: intl.formatMessage({ id: 'validation.reg_nr' }),
        };
      case 'postIndex':
        return {
          pattern: /^[A-Z]{2}-\d{4}$/,
          message: intl.formatMessage({ id: 'validation.postIndex' }),
        };
      case 'personalCode':
        return {
          pattern: /^\d{11}$/,
          message: intl.formatMessage({ id: 'validation.personal_code' }),
        };
      case 'email':
        return {
          pattern: /^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/,
          message: intl.formatMessage({ id: 'validation.email_not_valid' }),
        };
      case 'password':
        return {
          pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{10,}$/,
          message: intl.formatMessage({ id: 'validation.password_policy' }),
        };
      case 'phoneNumber':
        return {
          pattern: /^(?:\+?\d{11}|\d{8})$/,
          message: intl.formatMessage({
            id: 'validation.phone_number_not_valid',
          }),
        };
      case 'mapsApi':
        return {
          pattern:
            /^\s*-?(90(\.0+)?|[1-8]?\d(\.\d+)?)[ ]*,[ ]*-?(180(\.0+)?|1[0-7]\d(\.\d+)?|[1-9]?\d(\.\d+)?)\s*$/,
          message: intl.formatMessage({ id: 'validation.maps_api_point' }),
        };
      case 'minZeroNumber':
        return {
          validator: (_, value) => {
            if (value < 0) {
              return Promise.reject(
                new Error(
                  intl.formatMessage({ id: 'validation.negative_number' })
                )
              );
            }
            if (value !== 0 && !value) {
              return Promise.reject(
                new Error(intl.formatMessage({ id: 'validation.not_null' }))
              );
            }

            return Promise.resolve();
          },
        };
      case 'positiveNumber':
        return {
          validator: (_, value) => {
            if (value !== undefined && value !== null && value !== '') {
              const numValue = parseFloat(String(value));
              if (isNaN(numValue) || numValue <= 0) {
                return Promise.reject(
                  new Error(
                    intl.formatMessage({
                      id: 'validation.value_should_be_positive',
                    })
                  )
                );
              }
            }
            return Promise.resolve();
          },
        };
      case 'pvdNumber':
        return {
          pattern: /^\d{6}$/,
          message: intl.formatMessage({ id: 'validation.nvd_number' }),
        };
      case 'requiredText':
        return {
          type: 'string',
          required: true,
          message: intl.formatMessage({ id: 'validations.required_name' }),
        };
      case 'priceGreaterThanDiscount':
        return {
          validator: async (_, value) => {
            const discount = form.getFieldValue('storage_stock_discount_price');
            if (value !== null && discount !== null && value <= discount) {
              throw new Error(
                intl.formatMessage({ id: 'validation.price_must_be_greater' })
              );
            }
          },
        };
      case 'reemail':
        return ({ getFieldValue }) => ({
          validator(_, value) {
            if (!value || getFieldValue('email') === value) {
              return Promise.resolve();
            }
            return Promise.reject(
              new Error(intl.formatMessage({ id: 'register.email_mismatch' }))
            );
          },
        });
      case 'password_confirmation':
        return ({ getFieldValue }) => ({
          validator(_, value) {
            if (!value || getFieldValue('password') === value) {
              return Promise.resolve();
            }
            return Promise.reject(
              new Error(
                intl.formatMessage({ id: 'register.password_mismatch' })
              )
            );
          },
        });
      case 'minAmountNotExceedAvailability':
        return {
          validator: async (_, value) => {
            const step = form.getFieldValue('step');
            const availability = form.getFieldValue('availability');
            if (
              typeof value === 'number' &&
              typeof step === 'number' &&
              typeof availability === 'number'
            ) {
              if (value * step > availability) {
                throw new Error(
                  intl.formatMessage({
                    id: 'validation.min_amount_exceeds_availability',
                  })
                );
              }
            }
          },
        };
      case 'pickDateIfDiscount':
        return {
          validator: async (_, value) => {
            const discount = form.getFieldValue('storage_stock_discount_price');
            if (discount > 0 && !value) {
              return Promise.reject(
                new Error(
                  intl.formatMessage({
                    id: 'validation.date_required_if_discount',
                  })
                )
              );
            }
            return Promise.resolve();
          },
        };
      case 'correctStepForUnit':
        return {
          validator: async (_, value) => {
            const static_units = ['iep', 'gab'];
            const unit = form.getFieldValue('storage_unit');
            if (!static_units.includes(unit)) {
              return Promise.resolve();
            }
            if (static_units.includes(unit) && value % 1 !== 0) {
              return Promise.reject(
                new Error(
                  intl.formatMessage({ id: 'validation.decimal_number' })
                )
              );
            }
            if (value <= 0) {
              return Promise.reject(
                new Error(
                  intl.formatMessage({ id: 'validation.negative_number' })
                )
              );
            }
          },
        };
      case 'requiredIfNoNumberValuesAdded':
        return {
          validator: async (_, value) => {
            const nutritionalValues =
              form.getFieldValue('nutritional_values') || {};
            const ingredients = form.getFieldValue('ingredients') || '';

            const hasIngredients =
              typeof ingredients === 'string' && ingredients.trim() !== '';

            const hasAtLeastOneMacro = Object.values(nutritionalValues).some(
              (macroVal) =>
                macroVal !== undefined && macroVal !== null && macroVal !== ''
            );

            if (hasIngredients || hasAtLeastOneMacro) {
              return Promise.resolve();
            }

            if (
              value === undefined ||
              value === null ||
              value === '' ||
              (typeof value === 'number' && value < 0)
            ) {
              return Promise.reject(
                new Error(
                  intl.formatMessage({
                    id: 'validation.need_atleast_one_ingredient_or_macro',
                  })
                )
              );
            }

            return Promise.resolve();
          },
        };
      case 'farmBankAccount':
        return {
          validator: (_, value) => {
            if (!value || value.length === 0) {
              return Promise.resolve();
            }

            const lvIbanRegex = /^LV\d{2}[A-Z]{4}[A-Z0-9]{13}$/;

            const formattedValue = value.toUpperCase();

            if (!lvIbanRegex.test(formattedValue)) {
              return Promise.reject(
                new Error(
                  intl.formatMessage({ id: 'validation.invalid_bank_number' })
                )
              );
            }

            return Promise.resolve();
          },
        };
      case 'minLimitNotGreaterThanMax':
        return {
          validator: async (_, value) => {
            const storage_limit_min = form.getFieldValue('storage_limit_min');
            const storage_limit_max = form.getFieldValue('storage_limit_max');

            if (
              storage_limit_min !== undefined &&
              storage_limit_max !== undefined &&
              storage_limit_min > storage_limit_max
            ) {
              return Promise.reject(
                new Error(
                  intl.formatMessage({
                    id: 'validation.limit_min_grater_then_max',
                  })
                )
              );
            }

            return Promise.resolve();
          },
        };
      case 'onlyOneAndZero':
        return {
          validator: async (_, value) => {
            if (value < 0 || value > 1) {
              return Promise.reject(
                new Error(
                  intl.formatMessage({
                    id: 'validation.only_one_or_zero',
                  })
                )
              );
            }

            return Promise.resolve();
          },
        };
      case 'dateNotInPast':
        return {
          validator: async (_, value) => {
            if (value && value.isBefore(dayjs())) {
              return Promise.reject(
                new Error(
                  intl.formatMessage({
                    id: 'validation.date_too_soon',
                  })
                )
              );
            }
            return Promise.resolve();
          },
        };
      case 'endDateAfterStart':
        return {
          validator: async (_, value) => {
            if (value) {
              const startDate = form.getFieldValue('publication_start_date');
              if (
                startDate &&
                (value.isSame(startDate) || value.isBefore(startDate))
              ) {
                return Promise.reject(
                  new Error(
                    intl.formatMessage({
                      id: 'validation.end_date_after_start',
                    })
                  )
                );
              }
            }
            return Promise.resolve();
          },
        };
      case 'atLeastOneApplyTo':
        return ({ getFieldValue }) => ({
          validator() {
            const forEmarket = getFieldValue('for_emarket');
            const forWholesale = getFieldValue('for_wholesale');
            if (!forEmarket && !forWholesale) {
              return Promise.reject(
                new Error(
                  intl.formatMessage({ id: 'coupons.at_least_one_required' })
                )
              );
            }
            return Promise.resolve();
          },
        });
      case 'none':
        return { required: false };
    }

    return {};
  };

  const formValidations = (validations: string[] | string) => {
    if (typeof validations === 'string') {
      return [handleValidations(validations)];
    }

    return validations.map(handleValidations);
  };

  return { formValidations, nogaStorageStockCountNumberCheck, minMaxValidator };
}

export default useFormValidation;
